import { useState, useMemo, useCallback, useRef } from 'react';
import { ActionIcon, Button, Anchor, Text, Group, Checkbox, Tooltip } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import type { ColumnDef, Row } from '@tanstack/react-table';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import logger from '@ayocore/platform/common/helpers/logger';
import RepositoryTable from '@ayocore/platform/client/components/RepositoryTable/RepositoryTable';
import { TextCell, MoneyCell, DateCell } from '@ayocore/platform/client/components/RTable/cells';
import PartnerCell from '@ayocore/erp/crm/client/cells/PartnerCell';
import { useAccountingDocumentTransactionRepository } from '@ayocore/erp/accounting/client/rpc';
import type { AccountingDocumentTransaction } from '@ayocore/erp/accounting/schemas/document-transaction.schema';
import { SupplierDocumentView } from '@ayocore/erp/accounting/client/components/SupplierDocs/views/SupplierDocumentView';
import { CreateDealExpenseModal } from './expenses/CreateDealExpenseModal';
import { EditDealExpenseModal } from './expenses/EditDealExpenseModal';
import type { DealExtended } from '../views/DealViewController';

type DealExpenseRow = AccountingDocumentTransaction & {
  doc_number?: string | null;
  doc_operation_date?: string | null;
  doc_partner?: { id: number; name: string } | null;
  doc_total_amount?: number | null;
  doc_rest_amount?: number | null;
  doc_tax_amount?: number | null;
};

interface DealExpensesProps {
  deal: DealExtended;
  canEdit: boolean;
  /** Пересчитывает financialSummary сделки (маржу/прибыль) в DealViewController. */
  onReload?: () => void;
}

export function DealExpenses({ deal, canEdit, onReload }: DealExpensesProps) {
  const transactionRepository = useAccountingDocumentTransactionRepository();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const createExpenseModal = useModal(CreateDealExpenseModal);
  const editExpenseModal = useModal(EditDealExpenseModal);
  const docDrawer = useDrawer(SupplierDocumentView);

  const transactionRepoRef = useRef(transactionRepository);
  transactionRepoRef.current = transactionRepository;

  // Расход влияет на amount/approved сделки, поэтому каждый refresh() должен
  // пересчитывать financialSummary сделки (маржу/прибыль), а не только
  // локальную таблицу — иначе карточка «Прибыль» обновится лишь при
  // повторном открытии сделки.
  const refresh = () => {
    setRefreshTrigger(k => k + 1);
    onReload?.();
  };
  createExpenseModal.onClose(refresh);
  editExpenseModal.onClose(refresh);

  /** Удаляет привязку расхода к сделке. Проверяет canUpdate с маркером deal_expenses_tab. */
  const handleDelete = useCallback(async (rowId: number) => {
    try {
      // Проверяем можно ли удалить из этого UI
      const canResult = await transactionRepoRef.current.canUpdate(rowId, 'deal_expenses_tab');
      if (!canResult.allowed) {
        alert(canResult.message || 'Удаление из этого раздела невозможно');
        return;
      }
      if (!confirm('Удалить привязку расхода к сделке?')) return;
      await transactionRepoRef.current.deleteById(rowId);
      refresh();
    } catch (err) {
      logger.error('Ошибка при удалении расхода:', err);
      alert(err instanceof Error ? err.message : 'Ошибка при удалении');
    }
  }, []);

  /** Переключает согласование транзакции (approved_at). */
  const handleToggleApproved = useCallback(async (rowId: number, currentApprovedAt: unknown) => {
    const approved_at = currentApprovedAt ? null : new Date();
    await transactionRepoRef.current.update(rowId, { approved_at });
    refresh();
  }, []);

  const repository = useMemo(
    () =>
      transactionRepository
        .filterByDeal(deal.id)
        .withDocumentDetails()
        .orderBy('operation_date', 'asc')
        .orderBy('id', 'asc'),
    [transactionRepository, deal.id]
  );

  /** Переопределяем колонки — форматирование дат, сумм, ссылка на документ */
  const columns = useMemo(
    (): ColumnDef<DealExpenseRow>[] => [
      {
        id: 'approved',
        header: '',
        accessorKey: 'approved_at',
        size: 50,
        enableSorting: true,
        cell: ({ row }) => (
          <Checkbox
            checked={!!row.original.approved_at}
            onChange={() =>
              handleToggleApproved(row.original.id as number, row.original.approved_at)
            }
            color='green'
            size='sm'
            title='Согласовано'
            disabled={!canEdit}
          />
        ),
      },
      {
        id: 'doc_number',
        header: 'Номер документа',
        size: 180,
        enableSorting: true,
        cell: ({ row }) => {
          const documentId = row.original.document_id;
          return (
            <Anchor
              component='button'
              type='button'
              onClick={e => {
                e.stopPropagation();
                if (documentId) {
                  docDrawer.open({ documentId, isModal: true }, { size: 'xl' });
                }
              }}
            >
              {row.original.doc_number ?? '—'}
            </Anchor>
          );
        },
      },
      {
        id: 'doc_operation_date',
        header: 'Дата',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => <DateCell value={row.original.doc_operation_date} />,
      },
      {
        id: 'doc_partner',
        header: 'Партнёр',
        size: 200,
        enableSorting: true,
        cell: ({ row }) => <PartnerCell partner={row.original.doc_partner} />,
      },
      {
        id: 'doc_total_amount',
        header: 'Сумма документа',
        size: 200,
        enableSorting: true,
        cell: ({ row }) => {
          const { doc_total_amount, doc_rest_amount } = row.original;
          const hasRest = doc_rest_amount != null && doc_rest_amount !== 0;
          return (
            <Group gap={4} wrap='nowrap' justify='flex-end' style={{ width: '100%' }}>
              <MoneyCell value={doc_total_amount} />
              {hasRest && (
                <Text span size='xs' c='red' style={{ whiteSpace: 'nowrap' }}>
                  (<MoneyCell value={-(doc_rest_amount ?? 0)} />)
                </Text>
              )}
            </Group>
          );
        },
      },
      {
        id: 'doc_tax_amount',
        header: 'НДС',
        size: 140,
        enableSorting: true,
        cell: ({ row }) => <MoneyCell value={row.original.doc_tax_amount} />,
      },
      {
        id: 'amount',
        header: 'Привязано к сделке',
        size: 160,
        accessorKey: 'amount',
        enableSorting: true,
        cell: ({ row }) => (
          <Anchor
            component='button'
            type='button'
            style={{ display: 'block', width: '100%', textAlign: 'right' }}
            onClick={e => {
              e.stopPropagation();
              editExpenseModal.open({
                transactionId: row.original.id,
                title: row.original.doc_number
                  ? `Расход — ${row.original.doc_number}`
                  : 'Редактировать расход',
                onUpdated: () => editExpenseModal.close(),
              });
            }}
          >
            <MoneyCell value={row.original.amount} />
          </Anchor>
        ),
      },
      {
        id: 'description',
        header: 'Описание',
        size: 250,
        accessorKey: 'description',
        enableSorting: true,
        cell: ({ row }) => <TextCell value={row.original.description} />,
      },
      ...(canEdit
        ? [
            {
              id: 'actions',
              header: '',
              size: 50,
              enableSorting: false,
              cell: ({ row }: { row: Row<DealExpenseRow> }) => (
                <Tooltip label='Удалить привязку' withArrow>
                  <ActionIcon
                    size='xs'
                    color='red'
                    variant='subtle'
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleDelete(row.original.id as number);
                    }}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              ),
            } as ColumnDef<DealExpenseRow>,
          ]
        : []),
    ],
    [editExpenseModal, docDrawer, handleToggleApproved, handleDelete, canEdit]
  );

  return (
    <RepositoryTable
      id='crm-deal-expenses-inner'
      repository={repository}
      showSearch={false}
      refreshTrigger={refreshTrigger}
      columns={columns}
      tableBodyHeight='calc(100vh - 200px)'
      rowHeight={1}
      aggregates={[{ fn: 'sum', field: 'amount', label: 'Сумма расходов', color: '#faff60' }]}
      customActions={
        canEdit
          ? [
              <Button
                key='add'
                size='xs'
                variant='light'
                onClick={() =>
                  createExpenseModal.open({
                    dealId: deal.id,
                    title: 'Добавить расход',
                    onCreated: () => createExpenseModal.close(),
                  })
                }
              >
                Добавить
              </Button>,
            ]
          : []
      }
    />
  );
}
